import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SummaryRowBox,
  SummaryRowIcon,
  SummaryRowContent,
  SummaryRowLabel,
  SummaryRowValue,
  SummaryRowDetail,
  SummaryRowEditButton,
} from './styled';

interface SummaryRowProps {
  icon: ReactNode;
  label: string;
  section?: string;
  value: ReactNode;
  detail?: ReactNode;
  onEdit?: () => void;
  children?: ReactNode;
  // Keeps the row's height consistent with its neighbors when it sits next
  // to another row in a SummaryRowPair — without this, a longer detail
  // sentence wraps to extra lines once the row is squeezed to half width.
  truncateDetail?: boolean;
}

const slugify = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, '-');

const SummaryRow = ({
  icon,
  label,
  section,
  value,
  detail,
  onEdit,
  children,
  truncateDetail,
}: SummaryRowProps) => {
  const { t } = useTranslation('common');
  const testIdSection = section ?? slugify(label);

  return (
    <SummaryRowBox data-testid={`script-summary-row-${testIdSection}`}>
      <SummaryRowIcon>{icon}</SummaryRowIcon>
      <SummaryRowContent>
        <SummaryRowLabel>{label}</SummaryRowLabel>
        <SummaryRowValue>{value}</SummaryRowValue>
        {detail && (
          <SummaryRowDetail
            sx={
              truncateDetail
                ? {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }
                : undefined
            }
          >
            {detail}
          </SummaryRowDetail>
        )}
        {children}
      </SummaryRowContent>
      {onEdit && (
        <SummaryRowEditButton
          onClick={onEdit}
          size="small"
          data-testid={`script-summary-edit-${testIdSection}`}
        >
          {t('assistant.scriptDialog.wizard.editSection')}
        </SummaryRowEditButton>
      )}
    </SummaryRowBox>
  );
};

export default SummaryRow;
